import type { Contact } from '../../types';
import ContactPageHeader from './components/ContactPageHeader';
import ContactTable from './components/ContactTable';
import { ContactFilterChips } from './components/ContactFilterChips';
import ContactFormSheet from '../../components/shared/ContactFormSheet';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import BulkActionsBar from '../../components/shared/BulkActionsBar';
import Pagination from '../../components/shared/Pagination';
import ImportContactsModal from './components/ImportContactsModal';
import { useAppContext } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { notify } from '../../utils/toast';
import { contactPhoneKey } from '../../utils/phoneUtils';
import { useContactsPage } from './hooks/useContactsPage';
import { PageHeader } from '../../components/layout/Header';
import { MOBILE_CONTENT_BOTTOM_PAD_CLASS } from '../../components/layout/layoutNavTokens';
import { cn } from '@/lib/utils';

const DELETE_MODAL = { confirmButtonText: 'Excluir', confirmButtonColor: 'red' as const };

const FILTER_ROW = cn(
    'flex min-h-11 w-full min-w-0 flex-nowrap items-center gap-s2',
    'overflow-x-auto overflow-y-hidden overscroll-x-contain',
    '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
    'md:flex-wrap md:overflow-visible',
);

export default function ContactsFeature() {
    const { setCurrentPage } = useAppContext();
    const { canManageContacts, canExportData, isMember } = usePermissions();
    const { user } = useAuth();
    const page = useContactsPage(canExportData);
    const selectedCount = page.selectedIds.size;

    const handleChat = (contact: Contact) => {
        if (isMember) {
            const assignedTo = contact.assignedTo || contact.assigned_to;
            if (assignedTo && assignedTo !== user?.id) {
                notify.error('Este contato está atribuído a outro agente.');
                return;
            }
        }
        const rawPhone = contact.whatsapp || contact.phone;
        if (!rawPhone) {
            notify.warning(
                `Não foi possível abrir o chat para "${contact.name || 'contato'}". Nenhum número de telefone encontrado.`,
            );
            return;
        }
        const phone = contactPhoneKey(rawPhone);
        if (!phone) {
            notify.error('Telefone inválido.');
            return;
        }
        localStorage.setItem(
            'chat_auto_start',
            JSON.stringify({ number: phone, name: contact.name, timestamp: Date.now() }),
        );
        setCurrentPage('chat');
    };

    const handleExportOne = canExportData
        ? (contact: Contact) => page.handleExportOne(contact, 'csv')
        : undefined;

    const toolbarProps = {
        onAddContact: page.handleOpenCreateModal,
        onExportCsv: canExportData ? () => page.handleExportAll('csv') : undefined,
        onExportXlsx: canExportData ? () => page.handleExportAll('xlsx') : undefined,
        isExporting: page.isExporting,
        onImport: canManageContacts ? page.handleOpenImportModal : undefined,
        searchValue: page.filters.search,
        onSearchChange: page.updateSearch,
    };

    return (
        <>
            <main aria-label="Contatos" className="flex h-full flex-col bg-surface-1 md:bg-background">
                <header className="shrink-0 border-b border-border bg-surface-0 px-s4 pb-s3 pt-s3 md:border-0 md:bg-transparent md:px-s6 md:pb-0 md:pt-s4">
                    <PageHeader
                        title={
                            <>
                                Contatos{' '}
                                <span className="text-sm font-normal text-muted">{page.totalCount} contatos</span>
                            </>
                        }
                        subtitle="Todos os contatos dos seus leads."
                        className="mb-0 gap-s3 md:mb-s6 md:gap-s3"
                        titleClassName="text-lg md:text-xl"
                        subtitleClassName="text-sm text-muted"
                        hideBackButton
                    />
                </header>

                <section
                    aria-label="Lista de contatos"
                    className={cn(
                        'flex min-h-0 min-w-0 flex-1 flex-col gap-s3 overflow-x-hidden overflow-y-auto overscroll-y-contain px-s4 pt-s3 md:gap-s4 md:px-6 md:pb-s6 md:pt-0',
                        MOBILE_CONTENT_BOTTOM_PAD_CLASS,
                        selectedCount > 0 && 'max-md:pb-[max(9.5rem,calc(env(safe-area-inset-bottom)+8.5rem))]',
                    )}
                >
                    <section aria-label="Filtros e ações" className="flex min-w-0 w-full shrink-0 flex-col gap-s3">
                        <div className={FILTER_ROW}>
                            <ContactFilterChips
                                active={page.completenessChip}
                                onChange={page.setCompletenessChip}
                                counts={page.chipCounts}
                            />
                            <ContactPageHeader variant="desktop-inline" {...toolbarProps} />
                        </div>
                        <ContactPageHeader variant="mobile" {...toolbarProps} />
                    </section>

                    <div className="min-w-0 shrink-0">
                        <ContactTable
                            contacts={page.paginatedContacts}
                            isLoading={page.loading}
                            onEdit={page.handleOpenEditModal}
                            onDelete={page.handleOpenDeleteModal}
                            onDuplicate={page.handleDuplicateContact}
                            onChat={handleChat}
                            onExport={handleExportOne}
                            showCheckbox
                            selectedIds={page.selectedIds}
                            allSelected={page.isAllSelected}
                            someSelected={page.isSomeSelected}
                            onToggleSelection={page.toggleSelection}
                            onToggleAll={page.toggleAll}
                            footer={
                                <Pagination
                                    className="mt-0"
                                    currentPage={page.localCurrentPage}
                                    totalCount={page.filteredContacts.length}
                                    pageSize={page.pageSize}
                                    onPageChange={page.setLocalCurrentPage}
                                    onPageSizeChange={page.setPageSize}
                                    selectedCount={selectedCount}
                                />
                            }
                        />
                    </div>
                </section>
            </main>

            <BulkActionsBar selectedCount={selectedCount} actions={page.bulkActions} onClose={page.clearSelection} />

            <ContactFormSheet
                isOpen={page.isFormModalOpen}
                onClose={page.handleCloseModals}
                onSave={page.handleSaveContact}
                contact={page.contactToEdit}
            />

            <ConfirmationModal
                {...DELETE_MODAL}
                isOpen={page.isDeleteModalOpen}
                onClose={page.handleCloseModals}
                onConfirm={page.handleConfirmDelete}
                title="Excluir Contato"
                message={`Tem certeza que deseja excluir o contato "${page.contactToDelete?.name}"? Esta ação não pode ser desfeita.`}
            />

            <ConfirmationModal
                {...DELETE_MODAL}
                isOpen={page.isBulkDeleteModalOpen}
                onClose={() => page.setBulkDeleteModalOpen(false)}
                onConfirm={page.handleBulkDelete}
                title="Excluir Contatos"
                message={`Tem certeza que deseja excluir ${selectedCount} contato(s)? Esta ação não pode ser desfeita.`}
            />

            {canManageContacts && (
                <ImportContactsModal
                    isOpen={page.isImportModalOpen}
                    onClose={() => page.setImportModalOpen(false)}
                    onImportFinished={page.handleImportFinished}
                    createContactService={page.importContactService}
                />
            )}
        </>
    );
}
